import {
  Button,
  Stack,
  StackProps,
  UseDisclosureProps,
  useDisclosure,
} from '@chakra-ui/react';
import { RawDraftContentBlock, RawDraftContentState } from 'draft-js';
import * as React from 'react';

import { Redraft } from '@/components/Redraft';
import { RawDraftContentStateFragment } from '@/fragments/graphql-types/RawDraftContentStateFragment';
import { Prose } from '@/imports/ui/chakra/components/Prose';

export const AbbreviateRedraft: React.FC<
  StackProps & {
    options?: UseDisclosureProps;
    rawDraftContentState: RawDraftContentState | RawDraftContentStateFragment;
    noOfCharacters: number;
  }
> = ({ children, options, rawDraftContentState, noOfCharacters, ...props }) => {
  const disclosure = useDisclosure(options);

  const characterCount = React.useMemo(
    () =>
      // we have to cast to deal with the fact that we still use the
      // "RawDraftContentStateFragment" possibility
      (rawDraftContentState as RawDraftContentState).blocks.reduce(
        (previousValue, block) => previousValue + block.text.length,
        0
      ),
    [rawDraftContentState]
  );

  const isLongEnoughToBeAbbreviated = characterCount > noOfCharacters;

  const abbreviatedRawDraftContentState = React.useMemo(() => {
    let count = 0;
    const blocks: RawDraftContentBlock[] = [];
    for (let i = 0; i < rawDraftContentState.blocks.length; i++) {
      const block = rawDraftContentState.blocks[i];
      const remainingChars = noOfCharacters - count;

      if (count + block.text.length > noOfCharacters) {
        const overflowText = block.text.slice(remainingChars);
        const firstSpaceIndex = overflowText.indexOf(' ');

        if (firstSpaceIndex === -1) {
          const truncatedText = block.text.slice(0, remainingChars) + '...';
          blocks.push({
            ...block,
            text: truncatedText,
          });
        } else {
          const truncationIndex = remainingChars + firstSpaceIndex;
          const truncatedText = block.text.slice(0, truncationIndex) + '...';
          blocks.push({
            ...block,
            text: truncatedText,
          });
        }
        break;
      } else {
        blocks.push(block);
        count += block.text.length;
      }
    }

    return { ...rawDraftContentState, blocks } as RawDraftContentState;
  }, [noOfCharacters, rawDraftContentState]);

  return (
    <Stack {...props}>
      <Prose>
        <Redraft
          rawDraftContentState={
            disclosure.isOpen
              ? rawDraftContentState
              : abbreviatedRawDraftContentState
          }
        />
      </Prose>
      {isLongEnoughToBeAbbreviated && (
        <Button
          variant="link"
          colorScheme="black"
          onClick={disclosure.isOpen ? disclosure.onClose : disclosure.onOpen}
          alignSelf="flex-start"
        >
          {disclosure.isOpen ? 'See Less' : 'See More'}
        </Button>
      )}
    </Stack>
  );
};